// eslint-disable-next-line import/no-cycle
import {
  getChunkContainerElements,
} from '../../foreground/contentFramePortalGateInternalMethods';
import { getChunkScrollDepthInMobile } from '../../foreground/scrollDepth';
import { ChunkContainerElement } from '../../foreground/types/chunkedDocuments';
import { findScrollTargetInChunkContainer } from '../../foreground/utils/chunkedScrolling';
import { getIsCurrentlyUsingStylus } from '../../foreground/utils/detectIfIsCurrentlyUsingStylus';
import {
  findAllFocusableElements,
} from '../../foreground/utils/findAllFocusableElements';
import { findCenteredElementInViewport } from '../../foreground/utils/findCenteredElementInViewport';
import { makeWebviewLogger } from '../../foreground/utils/makeWebviewLogger';
import { isActuallyMovingWithStylus } from '../../foreground/utils/stylusEvents';
import { EpubLandmark, EpubPageListItem } from '../../types';
import { isHTMLElement } from '../../typeValidators';
import delay from '../../utils/delay';
import { handleCenteredElementDebug } from './centeredElementDebugUtils';
import { CLICKABLE_TAGS_SELECTOR } from './constants';
import { ScrollingManagerError } from './errors';
import { animateEndOfReadingButton } from './initEndOfReading';
import { NextChunkArrowHtml } from './NextChunkArrowHtml';
import { PreviousChunkArrowHtml } from './PreviousChunkArrowHtml';
// eslint-disable-next-line import/no-cycle
import { ScrollingManager, TouchEventDisabledSource } from './ScrollingManager';

const logger = makeWebviewLogger(__filename);

const BOTTOM_BAR_PLUS_TWO_LINES_OF_TEXT_APPROX_HEIGHT = 110;

export class ContinuousScrollingManager extends ScrollingManager {
  updateCenteredElementThrottleCounter = 0;
  currentlyScrollingBecauseOfTouch = false;
  currentHeight = 0;

  lastTouchStartTimestamp: number | undefined = undefined;
  loadingNextChunk = false;
  loadingPreviousChunk = false;
  hasTouchMoved = false;
  touchYDirection: number | null = null;
  startTouchClientY: number | null = null;
  startScrollOnTouch: number | null = null;
  endTouchClientY = 0;
  touchDirectionSwapCounter = 0;
  // Track haptic feedback intensity during a single swipe gesture
  // The last integer progress step (0-4) for which a light haptic was sent.
  private lastLightHapticStep = 0;
  // Whether a medium haptic has already been emitted during the ongoing gesture.
  private mediumHapticEmitted = false;

  // Tracks which arrow ("next" or "previous") is currently being animated during an
  // ongoing gesture. This prevents both arrows from showing/animating in a single
  // swipe gesture on very short pages where both edges are reachable.
  private activeArrow: 'next' | 'previous' | null = null;
  private edgeGlowMaskContainer: HTMLElement | null = null;

  // Distance (in pixels) that the user must swipe before a chunk transition is triggered.
  private get chunkTransitionTriggerDistancePx(): number {
    return 200;
  }

  // Maximum distance (in pixels) for edge glow effects and arrow scaling animations.
  // Set to 80% of the viewport height to provide a smooth visual experience.
  private get maxEdgeGlowTravelDistancePx(): number {
    return this.window.innerHeight * 0.8;
  }

// This class handles specific vertical scrolling functions
  init(): void {
    if (this.initialized) {
      return;
    }
    super.init();
    this.pageHeight = this.window.innerHeight;
    this.addEventListener(this.window, 'scroll', this.onScroll.bind(this));
    this.addEventListener(this.window, 'touchstart', this.onTouchStart.bind(this));
    this.addEventListener(this.window, 'touchmove', this.onTouchMove.bind(this));
    this.addEventListener(this.window, 'touchend', this.onTouchEnd.bind(this));
    if (!this.documentRoot) {
      throw new ScrollingManagerError('init: No document root found');
    }
    this.edgeGlowMaskContainer = this.document.getElementById('edge-glow-mask-container');

    this.initialized = true;
    this.currentHeight = this.documentTextContent?.getBoundingClientRect().height ?? 0;
    this.enableScrollEvents();
  }

  onRootClicked(e: Event) {
    // Explanation: click event target in the document content frame is always an Element
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const target = e.target as Element;
    if (target && isHTMLElement(target) && target.closest(CLICKABLE_TAGS_SELECTOR)) {
      return;
    }
    this.window.portalGateToForeground.emit('root-clicked');
  }

  destroy() {
    clearTimeout(this.scrollTimer);
    super.destroy();
    this.removeAllEventListeners();
    this.destroyNextChapterArrow();
    try {
      for (const chunk of getChunkContainerElements()) {
        this.clearAllVerticalChunkCSS(chunk);
      }
    } catch (e) {
      this.logger.debug('Destroy chunks error ', { e });
    }

    this.document.body.classList.remove('not-on-first-chunk');
  }

  async onResize() {
    if (!this.initialized) {
      return;
    }
    if (this.isOnResizeDisabled) {
      return;
    }
    const newHeight = this.documentTextContent?.getBoundingClientRect().height;
    if (this.documentTextContentHeight === newHeight || !newHeight) {
      return;
    }
    this.documentTextContentHeight = newHeight;
    logger.debug(`OnResize: fired , ${this.getScrollingElementTop()}`);
    if (this.getScrollingElementTop() <= 0) {
      // If we are at the top, we don't need to do anything
      return;
    }
    const scrollDelta = this.currentCenteredElementInfo?.scrollDelta;
    if (scrollDelta === undefined || !this.currentCenteredElementInfo.element) {
      logger.debug('OnResize failed due to no center element');
      return;
    }
    if (this.currentlyScrollingBecauseOfTouch) {
      logger.debug(
        `resize blocked, currentlyScrollingBecauseOfTouch: ${this.currentlyScrollingBecauseOfTouch};`,
      );
      return;
    }

    const element = this.currentCenteredElementInfo.element;
    const elementTop = this.getAbsoluteScrollTopOfRange(element);
    const y = elementTop - scrollDelta;
    this.setScrollingElementTop(Math.max(0, y));
  }

  updateCurrentCenteredElement() {
    logger.debug('UpdateCurrentCenteredElement fired');
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('UpdateCurrentCenteredElement Document Text Container not found');
    }
    if (!this.focusableElements.length) {
      this.focusableElements = findAllFocusableElements(this.documentTextContent);
    }

    const currentChunkContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    let centeredElement = findCenteredElementInViewport(
      this.focusableElements,
      this.window,
    );

    centeredElement ??= findScrollTargetInChunkContainer(currentChunkContainer);

    logger.debug('updateCurrentCenteredElement ', {
      centeredElement,
      top: centeredElement?.getBoundingClientRect().top,
    });

    handleCenteredElementDebug(centeredElement, this.window, this.document);

    // Uncomment for debug purposes
    // const prevElementDebug = this.document.querySelector('.centeredElementDebug');
    // prevElementDebug?.classList.remove('centeredElementDebug');
    // centeredElement?.classList.add('centeredElementDebug');
    // console.log("remember to comment me back out")

    const top = centeredElement?.getBoundingClientRect().top;
    this.currentCenteredElementInfo = {
      element: centeredElement,
      scrollDelta: top,
    };
  }

  isDocumentScrolledToBeginning(): boolean {
    const scrollableRoot = this.getScrollingElement();
    return scrollableRoot.scrollTop < 100;
  }

  async scrollToTop() {
    if (this.currentlyScrollingBecauseOfTouch) {
      return;
    }
    await this.setNewChunkIndex(0);
    super.scrollToTop();
  }

  async scrollToEndOfChunk() {
    const chunkElement = this.getChunkContainerByIndex(this.currentChunkIndex);
    this.setScrollingElementTop(chunkElement.scrollHeight);
  }

  async scrollToStartOfChunk() {
    this.setScrollingElementTop(0);
  }

  async scrollToStartOfDocument(landmarks?: EpubLandmark[], pageList?: EpubPageListItem[]) {
    try {
      await this.scrollToEpubStartPosition(landmarks, pageList);
    } catch {
      // Fallback to existing behavior
      await this.setNewChunkIndex(0);
      await this.scrollToTop();
    }
  }

  handleChunksLoading() {
    if (this.chunksLoading > 0) {
      this.disableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    } else {
      this.enableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    }
    this.setSpinnerEnabled(this.chunksLoading > 0, false);
  }


  async loadNextChunk() {
    if (this.loadingNextChunk || this.isCurrentChunkIndexFinal) {
      return;
    }
    this.disableScrollEvents();
    this.loadingNextChunk = true;
    const nextChunkIndex = Math.min(this.numberOfChunks - 1, this.currentChunkIndex + 1);
    const currentContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    const nextContainer = this.getChunkContainerByIndex(nextChunkIndex);
    currentContainer.style.opacity = '0';
    nextContainer.style.opacity = '0';
    await Promise.all([this.setNewChunkIndex(nextChunkIndex), delay(400)]);
    await this.scrollToStartOfChunk();
    nextContainer.style.opacity = '1';
    currentContainer.style.opacity = '1';
    this.loadingNextChunk = false;
    this.disableScrollEventsForNMilliseconds(1000);
  }

  async loadPreviousChunk() {
    if (this.loadingPreviousChunk || this.currentChunkIndex === 0) {
      return;
    }
    this.disableScrollEvents();
    this.loadingPreviousChunk = true;
    const previousChunkIndex = Math.max(0, this.currentChunkIndex - 1);
    const currentContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    const previousChunkContainer = this.getChunkContainerByIndex(previousChunkIndex);
    currentContainer.style.opacity = '0';
    previousChunkContainer.style.opacity = '0';
    await Promise.all([this.setNewChunkIndex(previousChunkIndex), delay(400)]);
    await this.scrollToEndOfChunk();
    previousChunkContainer.style.opacity = '1';
    currentContainer.style.opacity = '1';
    this.loadingPreviousChunk = false;
    this.disableScrollEventsForNMilliseconds(1000);
  }

  scrollToPosition(scrollTop: number) {
    const scrollingElement = this.getScrollingElement();
    scrollingElement.scrollTo({ top: scrollTop });
  }

  scrollViewportToCurrentTTSLocation(rect: DOMRect) {
    const offsetFromTop = 350;
    const scrollByAmount = rect.top - offsetFromTop;
    const scrollableRoot = this.getScrollingElement();
    const newScrollTop =
      Math.abs(scrollByAmount) > 100
        ? scrollableRoot.scrollTop + scrollByAmount
        : scrollableRoot.scrollTop;
    if (Math.abs(scrollByAmount) < 100) {
      return;
    }
    if (this.ttsAutoScrollingEnabled) {
      scrollableRoot.scrollTo({ top: newScrollTop, behavior: 'smooth' });
    }
  }

  async returnToReadingPosition() {
    if (this.readingPosition) {
      await this.scrollToReadingPosition(this.readingPosition);
    } else {
      this.scrollToTop();
    }
    await delay(4);

    const readingProgressData = await this.getReadingPositionData();
    this.window.portalGateToForeground.emit('return_to_reading_position', {
      currentScrollPositionData: this.getCurrentScrollPositionData(),
      currentReadingPositionData: readingProgressData,
      scrollDelta: 0,
    });
  }

  getScrollingElementMaxScroll() {
    const { scrollHeight } = this.getScrollingElement();
    return scrollHeight;
  }


  async onScrollStart() {
    this.updateCenteredElementThrottleCounter = 0;
    if (!this.scrollingEventsDisabled) {
      const readingProgressData = await this.getReadingPositionData();
      return this.window.portalGateToForeground.emit('scroll_start', {
        scrollDelta: 0,
        currentScrollPositionData: this.getCurrentScrollPositionData(),
        currentReadingPositionData: readingProgressData,
        startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
        endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
      });
    }
  }


  async onScroll() {
    const sourceEventTimestamp = this.lastTouchStartTimestamp;
    const { scrollTop: scrollTopFromElement, scrollHeight, clientHeight } = this.getScrollingElement();

    const maxScrollTop = this.getScrollingElementMaxScroll() - this.window.innerHeight;

    const scrollTop = Math.min(scrollTopFromElement, maxScrollTop);

    this.currentScrollValue = scrollTop;

    const readingProgressData = await this.getReadingPositionData();
    const scrollDelta = this.currentScrollValue - this.previousScrollValue;

    if (this.updateCenteredElementThrottleCounter === 0) {
      this.updateCurrentCenteredElement();
    }
    if (this.updateCenteredElementThrottleCounter > 2) {
      this.updateCenteredElementThrottleCounter = 0;
    }
    this.updateCenteredElementThrottleCounter += 1;


    const newIsScrollingDown = this.previousScrollValue < this.currentScrollValue;
    if (newIsScrollingDown !== this.isScrollingDown || !this.scrollTimer) {
      await this.onScrollStart();
    }

    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    this.isScrollingDown = newIsScrollingDown;
    this.previousScrollValue = this.currentScrollValue;

    if (!this.scrollingEventsDisabled) {
      await this.window.portalGateToForeground.emit('scroll', {
        currentReadingPositionData: readingProgressData,
        currentScrollPositionData: this.getCurrentScrollPositionData(),
        endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
        scrollDelta,
        sourceEventTimestamp,
        startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
      });
    }
    if (this.headerImageContainer && (scrollTop < -5 || this.headerImageContainer.style.transform !== 'scale(1)')) {
      const val = Math.min(3, 1 + -scrollTop * 0.005);
      this.headerImageContainer.style.transform = `scale(${Math.max(1, val)})`;
    }
    const scrollFromBottom = scrollHeight - scrollTop - clientHeight;

    // This check prevents animations before we have fully loaded the DOM
    if (!this.scrollingEventsDisabled && this.isCurrentChunkIndexFinal) {
      animateEndOfReadingButton(scrollFromBottom);
    }

    this.scrollTimer = setTimeout(this.onScrollEnd.bind(this), 100);
    for (const func of this.scrollListeners) {
      func({
        currentCenterElementInfo: this.currentCenteredElementInfo,
        currentChunkElement: this.getChunkContainerByIndex(this.currentChunkIndex),
      });
    }
  }

  async onScrollEnd() {
    try {
      logger.debug('OnScrollEnd fired');
      const sourceEventTimestamp = this.lastTouchStartTimestamp;
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
      this.updateCenteredElementThrottleCounter = 0;
      this.updateCurrentCenteredElement();
      if (!this.scrollingEventsDisabled) {
        const readingProgressData = await this.getReadingPositionData();
        const scrollDelta = this.currentScrollValue - this.previousScrollValue;
        const nrScrollEndPosition = this.getCurrentScrollPositionData();
        this.reportScrollEnded(nrScrollEndPosition.chunkIndex, nrScrollEndPosition.scrollDepthWithinChunk);
        await this.window.portalGateToForeground.emit('scroll_end', {
          currentReadingPositionData: readingProgressData,
          currentScrollPositionData: this.getCurrentScrollPositionData(),
          endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
          scrollDelta,
          sourceEventTimestamp,
          startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
        });
      }
    } catch (e) {
      logger.error('Scroll to end failed to report ', { e });
    }

    this.currentlyScrollingBecauseOfTouch = false;
    this.scrollTimer = undefined;
  }

  clearAllTouchData() {
    this.enableAllTouchEvents(TouchEventDisabledSource.Scroll);
    this.currentlyScrollingBecauseOfTouch = false;
    this.startTouchClientY = null;
    this.hasTouchMoved = false;
    this.touchMoveThrottle = 0;
    this.touchYDirection = 0;
    this.touchDirectionSwapCounter = 0;
    this.startScrollOnTouch = null;
    this.lastLightHapticStep = 0;
    this.mediumHapticEmitted = false;
    this.activeArrow = null;
    const arrowContainer = this.document.getElementById('nextChunkArrow') as HTMLElement | null;
    if (arrowContainer) {
      arrowContainer.style.transform = 'scale(1)';
      arrowContainer.style.opacity = '1';
    }

    const prevArrowContainer = this.document.getElementById('previousChunkArrow') as HTMLElement | null;
    if (prevArrowContainer) {
      prevArrowContainer.style.transform = 'scale(1)';
      prevArrowContainer.style.opacity = '0';
    }

    if (
      this.currentChunkIndex < this.numberOfChunks - 1 &&
      !this.document.getElementById('nextChunkArrow')
    ) {
      this.drawNextChapterArrow();
    }

    const currentChunkElement = this.getChunkContainerByIndex(this.currentChunkIndex);
    this._clearEdgeGlow();

    if (currentChunkElement) {
      currentChunkElement.style.transition =
        'opacity 0.2s ease-out, mask-size 0.2s ease-out';
      currentChunkElement.style.opacity = '1';
    }
  }

  onTouchStart(e: TouchEvent) {

    this.reportTap();
    // Unrounded for accuracy; not persisted to RxDB
    // eslint-disable-next-line no-restricted-syntax
    this.lastTouchStartTimestamp = Date.now();
    if (!this.documentRoot) {
      throw new ScrollingManagerError('ontouchStart, Document root not found');
    }
    if (e.touches.length === 1) {
      this.window.portalGateToForeground.emit('touch_start');
      this.hasTouchMoved = false;
      this.touchYDirection = 0;
      this.startTouchClientY = e.touches[0].clientY;
      this.endTouchClientY = e.touches[0].clientY;
      this.startScrollOnTouch = this.getScrollingElementTop();
    }
  }

  onTouchMove(e: TouchEvent) {
    if (this.ttsAutoScrollingEnabled) {
      this.window.portalGateToForeground.emit('set_tts_auto_scrolling_enabled', false);
    }
    if (this.startTouchClientY === null) {
      // should never happen
      return;
    }

    const touch = e.touches[0];

    if (getIsCurrentlyUsingStylus() && !isActuallyMovingWithStylus({ touchFromStylus: touch, startTouchClientY: this.startTouchClientY })) {
      return;
    }

    this.currentlyScrollingBecauseOfTouch = true;
    if (this.touchMoveThrottle === 0) {
      this.window.portalGateToForeground.emit('touch_move');
    }
    const newClientY = touch.clientY;
    this.touchMoveThrottle += 1;
    this.hasTouchMoved = true;
    if (this.touchMoveThrottle > 2) {
      // this.logger.log(`newTouch ${newClientY} oldTouch: ${this.ongoingTouchClientY} `);
      this.touchMoveThrottle = 0;
    }
    const touchYDelta = Math.abs(this.endTouchClientY - newClientY);
    if (touchYDelta > 0) {
      const direction = (this.endTouchClientY - newClientY) / touchYDelta;
      if (this.touchYDirection !== direction) {
        if (this.touchYDirection === 0) {
          this.touchYDirection = direction;
        }
        this.touchDirectionSwapCounter += 1;
        if (this.touchDirectionSwapCounter > 1) {
          this.touchDirectionSwapCounter = 0;
          this.touchYDirection = direction;
        }
      }
    }
    this.endTouchClientY = newClientY;
    if (this.enableScrollTimeout) {
      clearTimeout(this.enableScrollTimeout);
    }

    this.currentlyScrollingBecauseOfTouch = true;
    if (this.touchMoveThrottle === 0) {
      this.window.portalGateToForeground.emit('touch_move');
    }

    this.touchMoveThrottle += 1;
    if (this.touchMoveThrottle > 10) {
      this.touchMoveThrottle = 0;
    }

    this._updateNextChunkArrowScale();
    this._updatePreviousChunkArrowScale();
  }

  onTouchEnd(e: TouchEvent) {
    if (!this.startTouchClientY) {
      // should never happen
      this.clearAllTouchData();
      return;
    }
    const isTapOnScreen = !this.hasTouchMoved;
    if (isTapOnScreen) {
      this.onRootClicked(e);
      return;
    }
    if (!this.loadingPreviousChunk && !this.loadingNextChunk && this.startScrollOnTouch !== null) {
      const endScroll = this.getScrollingElementTop();
      const fingerTravelDistance = Math.abs((this.startTouchClientY ?? 0) - this.endTouchClientY);
      if (
        fingerTravelDistance >= this.chunkTransitionTriggerDistancePx &&
        endScroll <= 0 &&
        this.startScrollOnTouch <= 100 &&
        this.touchYDirection === -1
      ) {
        this.loadPreviousChunk();
      }
      if (
        // Make sure we ended at the bottom
        endScroll + this.window.innerHeight >= this.getScrollingElementMaxScroll() - 20 &&
        // make sure we started FROM the bottom
        this.startScrollOnTouch + this.window.innerHeight >= this.getScrollingElementMaxScroll() - 20 &&
        fingerTravelDistance >= this.chunkTransitionTriggerDistancePx &&
        this.touchYDirection === 1
      ) {
        this.loadNextChunk();
      }
    }
    this.clearAllTouchData();
  }

  instantlyScrollPageUp() {
    if (this._isScrolledToStartOfChunk() && this._canLoadPreviousChunk()) {
      this.loadPreviousChunk();
    } else {
      const scrollOffset = this.window.innerHeight - BOTTOM_BAR_PLUS_TWO_LINES_OF_TEXT_APPROX_HEIGHT;
      const positionToScroll = Math.max(0, this.currentScrollValue - scrollOffset);
      this.scrollToPosition(positionToScroll);
    }
  }

  instantlyScrollPageDown() {
    if (this._isScrolledToEndOfChunk() && this._canLoadNextChunk()) {
      this.loadNextChunk();
    } else {
      const scrollOffset = this.window.innerHeight - BOTTOM_BAR_PLUS_TWO_LINES_OF_TEXT_APPROX_HEIGHT;
      const maxScroll = this.getScrollingElementMaxScroll() - this.window.innerHeight;
      const positionToScroll = Math.min(maxScroll, this.currentScrollValue + scrollOffset);
      this.scrollToPosition(positionToScroll);
    }
  }

  clearAllVerticalChunkCSS(chunk: ChunkContainerElement) {
    chunk.style.display = '';
    chunk.style.minHeight = ``;
    chunk.style.display = '';
    chunk.style.minHeight = '';
    chunk.style.paddingTop = ``;
  }

  showOnlyCurrentChunk() {
    for (const chunk of getChunkContainerElements()) {
      if (Number(chunk.dataset.chunkIndex) === this.currentChunkIndex) {
        chunk.style.display = 'block';
        chunk.style.minHeight = `${this.window.innerHeight / 2}px`;
        if (Number(chunk.dataset.chunkIndex) !== 0) {
          chunk.style.paddingTop = `100px`;
        }
      } else {
        chunk.style.display = 'none';
        chunk.style.minHeight = '1px';
        chunk.style.paddingTop = `0px`;
      }
    }
    const endOfReadingContainer = this.window.document.getElementById('end-of-reading-container');
    if (!this.isCurrentChunkIndexFinal) {
      if (endOfReadingContainer) {
        endOfReadingContainer.style.display = 'none';
      }
    } else if (endOfReadingContainer) {
      endOfReadingContainer.style.display = 'block';
    }
  }

  async setNewChunkIndex(newIndex: number) {
    if (newIndex === this.currentChunkIndex) {
      const currentChunkElement = this.getChunkContainerByIndex(newIndex);
      await this._loadOnlyContentSurroundingChunkContainer(currentChunkElement);
      this.showOnlyCurrentChunk();
      return;
    }
    const newChunkIndex = Math.min(Math.max(0, newIndex), this.numberOfChunks - 1);
    const currentChunkElement = this.getChunkContainerByIndex(newChunkIndex);

    this._initEdgeGlowForChunk();

    if (newChunkIndex < this.numberOfChunks - 1) {
      this.drawNextChapterArrow();
    } else {
      this.destroyNextChapterArrow();
    }

    if (newChunkIndex > 0) {
      this.drawPreviousChapterArrow();
    } else {
      this.destroyPreviousChapterArrow();
    }

    await this._loadOnlyContentSurroundingChunkContainer(currentChunkElement);
    this.currentChunkIndex = newChunkIndex;
    if (newIndex > 0) {
      this.document.body.classList.add('not-on-first-chunk');
    } else {
      this.document.body.classList.remove('not-on-first-chunk');
    }

    if (newIndex > 0 && this.headerImageContainer && this.headerContainer) {
      this.headerImageContainer.style.display = 'none';
      this.headerImageContainer.style.opacity = '0';
      this.headerContainer.style.display = 'none';
    } else if (this.headerImageContainer && this.headerContainer) {
      this.headerImageContainer.style.display = 'block';
      this.headerContainer.style.display = 'block';
      setTimeout(() => {
        if (this.headerImageContainer) {
          this.headerImageContainer.style.opacity = '1';
        }
      }, 10);
    }
    this.showOnlyCurrentChunk();
    this.onContentChanged();
  }

  enableAllPaginationElements() {}

  disableAllPaginationElements() {}

  drawNextChapterArrow() {
    if (this.isCurrentChunkIndexFinal) {
      return;
    }
    if (this.document.getElementById('nextChunkArrow')) {
      return;
    }
    const arrowContainer = this.document.createElement('div');
    arrowContainer.id = 'nextChunkArrow';
    arrowContainer.classList.add('next-chunk-arrow-container');
    arrowContainer.innerHTML = NextChunkArrowHtml();
    // Append to .overflow-hidden-enforcer instead of documentRoot so it isn't affected by the
    // root mask.
    this.document.querySelector('.overflow-hidden-enforcer')?.appendChild(arrowContainer);
  }

  destroyNextChapterArrow() {
    const arrowContainer = this.document.getElementById('nextChunkArrow');
    if (!arrowContainer) {
      return;
    }
    arrowContainer.remove();
  }

  drawPreviousChapterArrow() {
    if (this.document.getElementById('previousChunkArrow')) {
      return;
    }
    const arrowContainer = this.document.createElement('div');
    arrowContainer.id = 'previousChunkArrow';
    arrowContainer.classList.add('previous-chunk-arrow-container');
    arrowContainer.innerHTML = PreviousChunkArrowHtml();
    // Append to .overflow-hidden-enforcer so it sits above the masked root.
    this.document.querySelector('.overflow-hidden-enforcer')?.appendChild(arrowContainer);
  }

  destroyPreviousChapterArrow() {
    const arrowContainer = this.document.getElementById('previousChunkArrow');
    if (arrowContainer) {
      arrowContainer.remove();
    }
  }

  getChunkScrollDepth(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect): number {
    return getChunkScrollDepthInMobile(currentChunkElement, serializedPositionElement);
  }

  async _scrollToRangeOrElement(
    target: HTMLElement | Range,
    offset = 150, // 150 because we pretty much NEVER want the element to be right at the very top of the screen...
    behavior: 'smooth' | 'auto' | 'instant' = 'instant',
  ) {
    let chunkContainer: ChunkContainerElement | undefined;
    if (target instanceof Range) {
      // NOTE: there's an edge case here where a range encompasses >1 chunk but we only get the first container
      chunkContainer = this.findChunkContainerForElement(this.getFirstNodeInRange(target));
    } else {
      chunkContainer = this.findChunkContainerForElement(target);
    }
    if (!chunkContainer) {
      return;
    }

    const newChunkIndex = parseInt(chunkContainer.dataset.chunkIndex, 10);
    await this.setNewChunkIndex(newChunkIndex);

    const y = this.getAbsoluteScrollTopOfRange(target);

    this.scrollingElementScrollTo({ top: y - offset, behavior });
  }

  async _scrollToScrollDepthPercentOfChunk(chunkIndex: number, scrollDepth: number) {
    await this.setNewChunkIndex(chunkIndex);
    const chunkContainer = this.getChunkContainerByIndex(chunkIndex);
    const chunkHeight = chunkContainer.getBoundingClientRect().height;
    const absoluteTopOfChunkContainer = this.getAbsoluteScrollTopOfRange(chunkContainer);
    const coordinateToScrollTo = absoluteTopOfChunkContainer + chunkHeight * scrollDepth;
    this.scrollingElementScrollTo({ top: coordinateToScrollTo, behavior: 'instant' });
  }

  _isScrolledToEndOfChunk(): boolean {
    const { scrollTop, scrollHeight, clientHeight } = this.getScrollingElement();
    return Math.abs(scrollTop + clientHeight - scrollHeight) <= 1;
  }

  private _isScrolledToStartOfChunk(): boolean {
    const { scrollTop } = this.getScrollingElement();
    return scrollTop <= 100;
  }

  private _canLoadNextChunk(): boolean {
    return !this.isCurrentChunkIndexFinal && !this.loadingNextChunk;
  }

  private _canLoadPreviousChunk(): boolean {
    return this.currentChunkIndex > 0 && !this.loadingPreviousChunk;
  }

  private _updatePreviousChunkArrowScale() {
    if (this.numberOfChunks <= 1) {
      return;
    }

    const atTop = this.startScrollOnTouch !== null && this.startScrollOnTouch <= 100;
    if (!atTop || this.currentChunkIndex === 0) {
      return;
    }

    if (this.touchYDirection !== -1 && this.activeArrow !== 'previous') {
      const prevArrow = this.document.getElementById('previousChunkArrow') as HTMLElement | null;
      if (prevArrow) {
        prevArrow.style.opacity = '0';
        prevArrow.style.transform = 'scale(1)';
      }
      return;
    }
    if (this.activeArrow && this.activeArrow !== 'previous') {
      return;
    }

    let arrowEl = this.document.getElementById('previousChunkArrow') as HTMLElement | null;
    if (!arrowEl) {
      if (this.touchYDirection !== -1) {
        return;
      }
      this.drawPreviousChapterArrow();
      arrowEl = this.document.getElementById('previousChunkArrow') as HTMLElement | null;
      if (!arrowEl) {
        return;
      }
      this.activeArrow = 'previous';
    } else if (!this.activeArrow) {
      this.activeArrow = 'previous';
    }
    const fingerTravelDistance = Math.abs((this.startTouchClientY ?? 0) - this.endTouchClientY);

    const progress = Math.min(1, fingerTravelDistance / this.chunkTransitionTriggerDistancePx);

    if (progress > 0) {
      this.disableAllTouchEvents(TouchEventDisabledSource.Scroll);
    }
    this._applyEdgeGlow('top', progress);
    this._applyScaleToArrow(arrowEl, progress, { fade: true });
    this._handleSwipeHapticFeedback(progress);
  }

  private _updateNextChunkArrowScale() {
    if (this.numberOfChunks <= 1) {
      return;
    }

    const atBottom =
      this.startScrollOnTouch !== null &&
      this.startScrollOnTouch + this.window.innerHeight >=
        this.getScrollingElementMaxScroll() - 20;
    if (!atBottom || this.isCurrentChunkIndexFinal) {
      return;
    }

    if (this.touchYDirection !== 1 && this.activeArrow !== 'next') {
      const nextArrow = this.document.getElementById('nextChunkArrow') as HTMLElement | null;
      if (nextArrow) {
        nextArrow.style.opacity = '0';
        nextArrow.style.transform = 'scale(1)';
      }
      return;
    }
    if (this.activeArrow && this.activeArrow !== 'next') {
      return;
    }

    let arrowEl = this.document.getElementById('nextChunkArrow') as HTMLElement | null;
    if (!arrowEl) {
      if (this.touchYDirection !== 1) {
        return;
      }
      this.drawNextChapterArrow();
      arrowEl = this.document.getElementById('nextChunkArrow') as HTMLElement | null;
      if (!arrowEl) {
        return;
      }
      this.activeArrow = 'next';
    } else if (!this.activeArrow) {
      this.activeArrow = 'next';
    }
    const fingerTravelDistance = Math.abs((this.startTouchClientY ?? 0) - this.endTouchClientY);

    const progress = Math.min(1, fingerTravelDistance / this.chunkTransitionTriggerDistancePx);

    if (progress > 0) {
      this.disableAllTouchEvents(TouchEventDisabledSource.Scroll);
    }

    this._applyEdgeGlow('bottom', progress);
    this._applyScaleToArrow(arrowEl, progress, { fade: false });
    this._handleSwipeHapticFeedback(progress);
  }

  private _applyScaleToArrow(
    arrowEl: HTMLElement,
    progress: number,
    { fade }: { fade: boolean; },
  ) {
    const maxScale = 1.3;
    const easedProgress = Math.min(1, Math.max(0, progress)) ** 2;
    const scale = 1 + easedProgress * (maxScale - 1);
    arrowEl.style.transform = `scale(${scale.toFixed(2)})`;
    if (fade) {
      arrowEl.style.opacity = progress.toString();
    }
  }

  private _handleSwipeHapticFeedback(progress: number) {
    if (progress >= 1) {
      if (!this.mediumHapticEmitted) {
        this.window.portalGateToForeground.emit('haptics_feedback', { style: 'heavy' });
        this.mediumHapticEmitted = true;
      }
      return;
    }

    const step = Math.floor(progress * 5);
    if (step > this.lastLightHapticStep) {
      this.window.portalGateToForeground.emit('haptics_feedback', { style: 'light' });
      this.lastLightHapticStep = step;
    }
  }

  private _applyEdgeGlow(edge: 'top' | 'bottom', progress: number) {
    const target = this.edgeGlowMaskContainer;
    if (!target) {
      return;
    }

    const maxGlowHeightPx = this.maxEdgeGlowTravelDistancePx;
    const maxGlowWidthPx = this.maxEdgeGlowTravelDistancePx;

    const heightRadius = Math.round(progress * maxGlowHeightPx);
    const widthRadius = Math.round(progress * maxGlowWidthPx);

    const widthDiameter = Math.min(widthRadius * 2, maxGlowWidthPx);
    const heightDiameter = Math.min(heightRadius * 2, maxGlowHeightPx);

    target.style.maskSize = `100% 100%, ${widthDiameter}px ${heightDiameter}px`;

    const anchor = edge === 'bottom' ? 'center bottom' : 'center top';
    target.style.maskPosition = `0 0, ${anchor}`;
  }

  private _clearEdgeGlow() {
    const target = this.edgeGlowMaskContainer;
    if (target) {
      target.style.maskSize = '100% 100%, 0px 0px';
    }
  }


  private _initEdgeGlowForChunk() {
    const rootEl = this.edgeGlowMaskContainer;
    if (!rootEl) {
      return;
    }
    rootEl.style.maskSize = '100% 100%, 0px 0px';
    rootEl.style.maskPosition = '0 0, center bottom';
  }
}
